import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Info as InfoIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';

function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    closeMobileMenu();
    navigate('/');
  };

  const menuItems = [
    { label: 'Programs', path: '/programs', icon: <SchoolIcon />, show: true },
    { label: 'About', path: '/about', icon: <InfoIcon />, show: true },
    { label: 'My Account', path: '/my-account', icon: <PersonIcon />, show: isAuthenticated },
    { label: 'Admin', path: '/admin', icon: <AdminIcon />, show: isAdmin },
  ];

  const authButtons = isAuthenticated
    ? [{ label: 'Logout', onClick: handleLogout, icon: <LogoutIcon />, variant: 'outlined', color: 'error' }]
    : [
        { label: 'Login', path: '/login', icon: <LoginIcon />, variant: 'outlined' },
        { label: 'Create Account', path: '/create-account', icon: <PersonAddIcon />, variant: 'contained' },
      ];

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          background: 'linear-gradient(135deg, #2d5a42 0%, #1e4034 100%)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            {/* Logo/Brand */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                '&:hover': {
                  opacity: 0.8,
                },
                transition: 'opacity 0.3s ease',
              }}
            >
              <img
                src="/logo-wide.svg"
                alt="MVYP Logo"
                style={{
                  height: '100px',
                  width: 'auto',
                }}
              />
            </Box>

            {/* Desktop Menu */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {menuItems
                  .filter((item) => item.show)
                  .map((item) => (
                    <Button
                      key={item.path}
                      component={RouterLink}
                      to={item.path}
                      startIcon={item.icon}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                {authButtons.map((button, index) =>
                  button.onClick ? (
                    <Button
                      key={index}
                      onClick={button.onClick}
                      startIcon={button.icon}
                      variant={button.variant}
                      color={button.color}
                      sx={{
                        fontWeight: 600,
                        ml: 1,
                      }}
                    >
                      {button.label}
                    </Button>
                  ) : (
                    <Button
                      key={index}
                      component={RouterLink}
                      to={button.path}
                      startIcon={button.icon}
                      variant={button.variant}
                      color={button.color || 'secondary'}
                      sx={{
                        fontWeight: 600,
                        ml: 1,
                      }}
                    >
                      {button.label}
                    </Button>
                  )
                )}
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open menu"
                edge="end"
                onClick={toggleMobileMenu}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            background: 'linear-gradient(135deg, #2d5a42 0%, #1e4034 100%)',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <img
            src="/logo.svg"
            alt="MVYP Logo"
            style={{
              height: '40px',
              width: 'auto',
            }}
          />
          <IconButton onClick={closeMobileMenu} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {menuItems
            .filter((item) => item.show)
            .map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  onClick={closeMobileMenu}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          {authButtons.map((button, index) =>
            button.onClick ? (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  onClick={() => {
                    button.onClick();
                    closeMobileMenu();
                  }}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>{button.icon}</Box>
                  <ListItemText primary={button.label} />
                </ListItemButton>
              </ListItem>
            ) : (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={button.path}
                  onClick={closeMobileMenu}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>{button.icon}</Box>
                  <ListItemText primary={button.label} />
                </ListItemButton>
              </ListItem>
            )
          )}
        </List>
      </Drawer>

      {/* Spacer to prevent content from going under fixed AppBar */}
      <Toolbar />
    </>
  );
}

export default Navbar;
